import { useEffect, useRef, useState } from 'react'

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/ui/molecules/carousel/Carousel'
import { cn } from '@/ui/utils/style'

const stepsUrls = [
  'https://cdn.spark.fi/base-mini-app/tutorial/1.mp4',
  'https://cdn.spark.fi/base-mini-app/tutorial/2.mp4',
  'https://cdn.spark.fi/base-mini-app/tutorial/3.mp4',
  'https://cdn.spark.fi/base-mini-app/tutorial/4.mp4',
]
const stepsTitles = [
  'Scroll down & tap “Deposit”',
  'Enter amount, tap “Execute” & “Confirm”',
  'Confirm successful deposit',
  'See your growing balance',
]

const LOOP_DELAY_MS = 2000

export function TutorialCarousel() {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const timeoutRefs = useRef<(NodeJS.Timeout | null)[]>([])
  const handlerRefs = useRef<Array<(() => void) | null>>([])

  useEffect(() => {
    if (!carouselApi) {
      return
    }

    setCurrent(carouselApi.selectedScrollSnap())

    // eslint-disable-next-line func-style
    const handleSelect = () => {
      const selectedIndex = carouselApi.selectedScrollSnap()
      setCurrent(selectedIndex)

      clearAllTimeouts(timeoutRefs.current)
      timeoutRefs.current = []
      pauseAllVideos(videoRefs.current)

      const selectedVideo = videoRefs.current[selectedIndex]
      if (selectedVideo) {
        playVideoFromStart(selectedVideo)
      }
    }

    carouselApi.on('select', handleSelect)
    handleSelect()

    return () => {
      carouselApi.off('select', handleSelect)
      clearAllTimeouts(timeoutRefs.current)
    }
  }, [carouselApi])

  useEffect(() => {
    if (!carouselApi) {
      return
    }

    // eslint-disable-next-line func-style
    const handleVideoEnd = (index: number) => {
      return () => {
        const video = videoRefs.current[index]
        if (!video) {
          return
        }

        if (timeoutRefs.current[index]) {
          clearTimeout(timeoutRefs.current[index]!)
        }

        video.pause()

        timeoutRefs.current[index] = setTimeout(() => {
          if (video && carouselApi.selectedScrollSnap() === index) {
            playVideoFromStart(video)
          }
        }, LOOP_DELAY_MS)
      }
    }

    // eslint-disable-next-line func-style
    const cleanupVideoListeners = () => {
      videoRefs.current.forEach((video, index) => {
        if (video && handlerRefs.current[index]) {
          video.removeEventListener('ended', handlerRefs.current[index]!)
        }
        if (timeoutRefs.current[index]) {
          clearTimeout(timeoutRefs.current[index]!)
        }
      })
    }

    // eslint-disable-next-line func-style
    const setupVideoListeners = () => {
      cleanupVideoListeners()
      handlerRefs.current = []
      timeoutRefs.current = []

      videoRefs.current.forEach((video, index) => {
        if (video) {
          const handler = handleVideoEnd(index)
          handlerRefs.current[index] = handler
          video.addEventListener('ended', handler)
        } else {
          handlerRefs.current[index] = null
        }
      })
    }

    setupVideoListeners()

    const retryTimeoutId = setTimeout(() => {
      setupVideoListeners()
    }, 0)

    return () => {
      clearTimeout(retryTimeoutId)
      cleanupVideoListeners()
    }
  }, [carouselApi])

  return (
    <div className="mx-auto w-full max-w-md">
      <Carousel setApi={setCarouselApi} className="w-full">
        <CarouselContent>
          {stepsUrls.map((url, index) => (
            <CarouselItem key={index}>
              <div
                className="aspect-335/466 w-full overflow-hidden rounded-md border border-primary"
                data-chromatic="ignore"
              >
                <video
                  ref={(el) => {
                    videoRefs.current[index] = el
                  }}
                  className="h-full w-full object-cover"
                  playsInline
                  muted
                >
                  <source src={url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="mt-4 flex items-center justify-between">
          <CarouselPrevious className={cn('static top-auto right-auto left-auto translate-x-0 translate-y-0')} />
          <CarouselNext className={cn('static top-auto right-auto left-auto translate-x-0 translate-y-0')} />
        </div>
      </Carousel>
      <div className="flex flex-col items-center justify-center">
        <div className="typography-label-3 text-secondary">Step {current + 1}</div>
        <div className="typography-label-1 mt-1 text-primary">{stepsTitles[current]}</div>
        <div className="mt-4 flex w-full items-center gap-1">
          {stepsUrls.map((_, index) => (
            <div
              key={index}
              className={cn(
                'h-0.5 flex-1 transition-colors',
                index === current ? 'bg-fg-system-success-primary' : 'bg-tertiary',
              )}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function clearAllTimeouts(timeouts: (NodeJS.Timeout | null)[]): void {
  timeouts.forEach((timeout) => {
    if (timeout) {
      clearTimeout(timeout)
    }
  })
}

function pauseAllVideos(videos: (HTMLVideoElement | null)[]): void {
  videos.forEach((video) => {
    if (video) {
      video.pause()
    }
  })
}

function playVideoFromStart(video: HTMLVideoElement): void {
  video.currentTime = 0
  void video.play()
}
